import { useState, useEffect } from "react";

/* Zeroed baseline shown to a brand-new user (or before stats have loaded).
 * Everything is driven by real activity — no placeholder achievements. */
const EMPTY_STATS = {
  totalSessions: 0,
  totalHours: 0,
  streak: 0,
  avgScore: 0,
  examsTaken: 0,
  notebooksStudied: 0,
  weakAreas: ["None identified yet. Complete quizzes or exams to pinpoint areas for improvement."],
  strongAreas: ["No strong areas recorded yet. Keep studying and score 80%+ on assessments!"],
  recentExams: [],
  weeklyData: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map(day => ({ day, hours: 0, score: 0 })),
};

function StatCard({ icon, iconColor, iconBg, label, value, sub }) {
  return (
    <div style={{
      background: "var(--surface)",
      border: "1px solid var(--border)",
      borderRadius: "var(--radius-xl)",
      padding: "16px 18px",
    }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 12 }}>
        <div style={{
          width: 36, height: 36, borderRadius: 10,
          background: iconBg, display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <span className="material-symbols-rounded" style={{ fontSize: 18, color: iconColor, fontVariationSettings: "'FILL' 1" }}>
            {icon}
          </span>
        </div>
      </div>
      <div style={{ fontSize: 24, fontWeight: 700, letterSpacing: -0.5, color: "var(--ink)", marginBottom: 3 }}>{value}</div>
      <div style={{ fontSize: 12, fontWeight: 600, color: "var(--ink-2)", marginBottom: 2 }}>{label}</div>
      {sub && <div style={{ fontSize: 11, color: "var(--ink-3)" }}>{sub}</div>}
    </div>
  );
}

function BarChart({ data }) {
  const maxH = Math.max(...data.map(d => d.hours));
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "flex-end", height: 80 }}>
      {data.map((d, i) => (
        <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
          <div style={{ fontSize: 10, color: "var(--ink-3)", fontWeight: 500 }}>
            {d.hours > 0 ? d.hours + "h" : ""}
          </div>
          <div
            style={{
              width: "100%",
              height: maxH > 0 ? (d.hours / maxH) * 56 : 4,
              minHeight: 4,
              borderRadius: "var(--radius-sm) var(--radius-sm) 0 0",
              background: d.hours > 0
                ? "linear-gradient(180deg, var(--accent), var(--sky))"
                : "var(--surface-3)",
              transition: "height 0.5s ease",
            }}
          />
          <div style={{ fontSize: 10, color: "var(--ink-3)" }}>{d.day}</div>
        </div>
      ))}
    </div>
  );
}

export default function ProgressPage({ notebooks = [], progressStats, onGoExam }) {
  // If we have actual progress stats from backend, use them to build dynamic report
  const stats = (() => {
    if (!progressStats) return EMPTY_STATS;

    // Estimate hours: 30m per study session, 20m per quiz/exam, 1.2m per chat message
    const hoursVal = (
      (progressStats.total_study_sessions || 0) * 0.5 +
      (progressStats.total_quizzes || 0) * 0.3 +
      (progressStats.messages_sent || 0) * 0.02
    );
    const totalHours = Math.round(hoursVal * 10) / 10;

    // Determine weak areas from real list
    const weakAreas = progressStats.weak_areas?.length
      ? progressStats.weak_areas
      : ["None identified yet. Complete quizzes or exams to pinpoint areas for improvement."];

    // Deduce strong areas: topics with scores >= 80%
    const rawStrong = progressStats.quiz_scores
      ?.filter(s => s.percent >= 80)
      .map(s => s.topic) || [];
    const strongAreas = Array.from(new Set(rawStrong));
    if (strongAreas.length === 0) {
      strongAreas.push("No strong areas recorded yet. Keep studying and score 80%+ on assessments!");
    }

    // Map quiz scores to recent exams
    const recentExams = [...(progressStats.quiz_scores || [])]
      .reverse()
      .map(s => {
        let dateStr = "Recent";
        try {
          if (s.ts) {
            dateStr = new Intl.DateTimeFormat("en-IN", { day: "numeric", month: "short" }).format(new Date(s.ts));
          }
        } catch {}
        return {
          date: dateStr,
          subject: s.topic || "Practice Quiz",
          score: s.score,
          total: s.total,
          type: s.topic?.includes("Exam") ? "Exam Attempt" : "Topic Quiz"
        };
      })
      .slice(0, 10); // cap at 10

    // Distribute hours across days of the week based on activity timestamps
    const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
    const weeklyMap = { Mon: { h: 0, s: 0, c: 0 }, Tue: { h: 0, s: 0, c: 0 }, Wed: { h: 0, s: 0, c: 0 }, Thu: { h: 0, s: 0, c: 0 }, Fri: { h: 0, s: 0, c: 0 }, Sat: { h: 0, s: 0, c: 0 }, Sun: { h: 0, s: 0, c: 0 } };

    // Fill daily activity from real scores
    if (progressStats.quiz_scores?.length) {
      progressStats.quiz_scores.forEach(s => {
        try {
          if (s.ts) {
            const dayName = new Intl.DateTimeFormat("en-US", { weekday: "short" }).format(new Date(s.ts));
            if (weeklyMap[dayName]) {
              weeklyMap[dayName].h += 0.4; // 24 mins per quiz/exam
              weeklyMap[dayName].s += s.percent || (s.score / Math.max(s.total, 1) * 100);
              weeklyMap[dayName].c += 1;
            }
          }
        } catch {}
      });
    }

    // Add study sessions estimated time to last active day or spread
    try {
      if (progressStats.last_active) {
        const lastDay = new Intl.DateTimeFormat("en-US", { weekday: "short" }).format(new Date(progressStats.last_active));
        if (weeklyMap[lastDay]) {
          weeklyMap[lastDay].h += (progressStats.total_study_sessions || 0) * 0.3; // add study session time
        }
      }
    } catch {}

    const weeklyData = days.map(d => {
      const data = weeklyMap[d];
      return {
        day: d,
        hours: Math.round(data.h * 10) / 10,
        score: data.c > 0 ? Math.round(data.s / data.c) : 0
      };
    });

    return {
      totalSessions: progressStats.total_study_sessions || 0,
      totalHours,
      streak: progressStats.streak || 0,
      avgScore: Math.round(progressStats.average_quiz_score || 0),
      examsTaken: progressStats.total_quizzes || 0,
      notebooksStudied: progressStats.notebooks_count || notebooks?.length || 0,
      weakAreas,
      strongAreas,
      recentExams,
      weeklyData
    };
  })();

  const [tab, setTab] = useState("overview");

  const streakMessage = (() => {
    const s = stats.streak;
    if (s === 0) return "Start your study streak today! Complete a lesson, quiz, or exam to begin.";
    if (s === 1) return "Great start! You've begun your learning streak. Let's keep it going tomorrow!";
    if (s === 2) return "2 days in a row! You are building a powerful study habit.";
    if (s === 3) return "3-day streak! You are starting to catch fire. Keep the momentum going! 🔥";
    if (s === 4) return "4 days of dedication! Your persistence is paving the way for success.";
    if (s === 5) return "5-day study streak! Phenomenal focus! You're doing absolutely amazing.";
    if (s === 6) return "6 days in a row! Just one more day to complete a full week of consistent learning!";
    if (s === 7) return "Keep it up! You've studied every single day this week. Perfect score! 🌟";
    return `Phenomenal! A ${s}-day study streak is absolutely outstanding. You are completely unstoppable! 🚀`;
  })();

  return (
    <div style={{ flex: 1, overflowY: "auto", background: "var(--bg)" }}>
      <div style={{ maxWidth: 900, margin: "0 auto", padding: "24px 28px" }}>

        {/* Header */}
        <div style={{ marginBottom: 24, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <h2 style={{ fontSize: 20, fontWeight: 700, letterSpacing: -0.5, marginBottom: 4 }}>
              Study Progress
            </h2>
            <p style={{ fontSize: 13, color: "var(--ink-3)" }}>
              Track your performance across all notebooks and exams.
            </p>
          </div>
          <button onClick={onGoExam} className="btn btn-primary" style={{ gap: 7 }}>
            <span className="material-symbols-rounded" style={{ fontSize: 16, fontVariationSettings: "'FILL' 1" }}>edit_note</span>
            Take Exam
          </button>
        </div>

        {/* Streak banner */}
        <div style={{
          background: "linear-gradient(135deg, rgba(99,102,241,0.12), rgba(6,182,212,0.08))",
          border: "1px solid rgba(99,102,241,0.2)",
          borderRadius: "var(--radius-xl)",
          padding: "16px 20px",
          display: "flex", alignItems: "center", gap: 14,
          marginBottom: 20,
        }}>
          <span className="material-symbols-rounded" style={{ fontSize: 32, color: "var(--amber)", fontVariationSettings: "'FILL' 1" }}>
            local_fire_department
          </span>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "var(--ink)" }}>
              {stats.streak}-day study streak 🔥
            </div>
            <div style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 2 }}>
              {streakMessage}
            </div>
          </div>
          <div style={{ marginLeft: "auto", display: "flex", gap: 5 }}>
            {Array.from({ length: 7 }, (_, i) => (
              <div key={i} style={{
                width: 24, height: 24, borderRadius: 6,
                background: i < stats.streak ? "var(--amber)" : "var(--surface-3)",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 12,
              }}>
                {i < stats.streak ? "✓" : ""}
              </div>
            ))}
          </div>
        </div>

        {/* Stat cards */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 12, marginBottom: 20 }}>
          <StatCard icon="schedule"      iconColor="var(--accent-2)" iconBg="var(--accent-soft)" label="Study Hours"     value={`${stats.totalHours}h`}  sub="this month" />
          <StatCard icon="edit_note"     iconColor="var(--green)"    iconBg="var(--green-soft)"  label="Exams Taken"     value={stats.examsTaken}         sub="all time" />
          <StatCard icon="percent"       iconColor="var(--amber)"    iconBg="var(--amber-soft)"  label="Avg. Score"      value={`${stats.avgScore}%`}     sub="across all exams" />
          <StatCard icon="menu_book"     iconColor="var(--purple)"   iconBg="var(--purple-soft)" label="Notebooks"       value={notebooks?.length || stats.notebooksStudied} sub="indexed" />
        </div>

        {/* Two col layout */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>

          {/* Weekly activity */}
          <div style={{
            background: "var(--surface)",
            border: "1px solid var(--border)",
            borderRadius: "var(--radius-xl)",
            padding: "18px 20px",
          }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 16, color: "var(--ink)" }}>
              Weekly Activity
            </div>
            <BarChart data={stats.weeklyData} />
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 12, fontSize: 11, color: "var(--ink-3)" }}>
              <span>Study hours per day</span>
              <span>{stats.weeklyData.reduce((a, d) => a + d.hours, 0).toFixed(1)}h total</span>
            </div>
          </div>

          {/* Score distribution */}
          <div style={{
            background: "var(--surface)",
            border: "1px solid var(--border)",
            borderRadius: "var(--radius-xl)",
            padding: "18px 20px",
          }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 16, color: "var(--ink)" }}>
              Score Distribution
            </div>
            {stats.recentExams.length === 0 ? (
              <div style={{ fontSize: 12, color: "var(--ink-3)", textAlign: "center", padding: "30px 0" }}>
                No score data available. Complete an exam or quiz first!
              </div>
            ) : (
              stats.recentExams.map((ex, i) => {
                const pct = Math.round((ex.score / ex.total) * 100);
                const color = pct >= 75 ? "var(--green)" : pct >= 60 ? "var(--amber)" : "var(--rose)";
                return (
                  <div key={i} style={{ marginBottom: 10 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, fontSize: 12 }}>
                      <span style={{ color: "var(--ink-2)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 180 }}>
                        {ex.subject}
                      </span>
                      <span style={{ color, fontWeight: 600, flexShrink: 0 }}>{pct}%</span>
                    </div>
                    <div className="progress-track" style={{ height: 5 }}>
                      <div style={{
                        height: "100%", borderRadius: "var(--radius-full)",
                        width: `${pct}%`, background: color,
                        transition: "width 0.5s ease",
                      }} />
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Weak / strong areas */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
          <div style={{
            background: "var(--surface)",
            border: "1px solid rgba(244,63,94,0.15)",
            borderRadius: "var(--radius-xl)",
            padding: "16px 18px",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 12 }}>
              <span className="material-symbols-rounded" style={{ fontSize: 17, color: "var(--rose)", fontVariationSettings: "'FILL' 1" }}>
                warning
              </span>
              <span style={{ fontSize: 13, fontWeight: 600, color: "var(--ink)" }}>Weak Areas</span>
            </div>
            {stats.weakAreas.map((area, i) => (
              <div key={i} style={{
                display: "flex", alignItems: "center", gap: 8,
                padding: "7px 10px", borderRadius: "var(--radius-md)",
                background: "var(--rose-soft)", marginBottom: 5,
              }}>
                <span className="material-symbols-rounded" style={{ fontSize: 14, color: "var(--rose)" }}>trending_down</span>
                <span style={{ fontSize: 12.5, color: "var(--ink-2)" }}>{area}</span>
              </div>
            ))}
          </div>

          <div style={{
            background: "var(--surface)",
            border: "1px solid rgba(16,185,129,0.15)",
            borderRadius: "var(--radius-xl)",
            padding: "16px 18px",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 12 }}>
              <span className="material-symbols-rounded" style={{ fontSize: 17, color: "var(--green)", fontVariationSettings: "'FILL' 1" }}>
                verified
              </span>
              <span style={{ fontSize: 13, fontWeight: 600, color: "var(--ink)" }}>Strong Areas</span>
            </div>
            {stats.strongAreas.map((area, i) => (
              <div key={i} style={{
                display: "flex", alignItems: "center", gap: 8,
                padding: "7px 10px", borderRadius: "var(--radius-md)",
                background: "var(--green-soft)", marginBottom: 5,
              }}>
                <span className="material-symbols-rounded" style={{ fontSize: 14, color: "var(--green)" }}>trending_up</span>
                <span style={{ fontSize: 12.5, color: "var(--ink-2)" }}>{area}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Recent exams table */}
        <div style={{
          background: "var(--surface)",
          border: "1px solid var(--border)",
          borderRadius: "var(--radius-xl)",
          padding: "18px 20px",
        }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 14, color: "var(--ink)" }}>
            Recent Exams
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {stats.recentExams.length === 0 ? (
              <div style={{
                textAlign: "center", padding: "40px 16px", color: "var(--ink-3)", fontSize: 13
              }}>
                <span className="material-symbols-rounded" style={{ fontSize: 32, color: "var(--ink-3)", display: "block", marginBottom: 10 }}>
                  assignment_late
                </span>
                No exam history found. Use the Take Exam button above or quiz yourself in the workspace to see your history!
              </div>
            ) : (
              stats.recentExams.map((ex, i) => {
                const pct = Math.round((ex.score / ex.total) * 100);
                const color = pct >= 75 ? "var(--green)" : pct >= 60 ? "var(--amber)" : "var(--rose)";
                return (
                  <div key={i} style={{
                    display: "flex", alignItems: "center", gap: 14,
                    padding: "10px 12px",
                    borderRadius: "var(--radius-lg)",
                    background: "var(--surface-2)",
                    border: "1px solid var(--border)",
                  }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: 9, flexShrink: 0,
                      background: `${color}18`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      <span className="material-symbols-rounded" style={{ fontSize: 17, color, fontVariationSettings: "'FILL' 1" }}>
                        edit_note
                      </span>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {ex.subject}
                      </div>
                      <div style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 1 }}>
                        {ex.type} · {ex.date}
                      </div>
                    </div>
                    <div style={{
                      fontSize: 14, fontWeight: 700, color,
                      background: `${color}18`, padding: "3px 10px",
                      borderRadius: "var(--radius-full)",
                      flexShrink: 0,
                    }}>
                      {ex.score}/{ex.total}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
}